/**
 * ClinicCaseWorkflow — FE-WIRING-1B
 * ===================================
 *
 * Clinic / coordinator canonical operational view for a single
 * `treatment_cases.case_id`.
 *
 * AUTHORITY (FE-CONTRACT-LOCK):
 *   - Sole authority: canonical operational chain
 *     `treatment_cases → milestone_instances → milestone_tasks → assignment_log`
 *     (COM-1D.D / COM-1D.D2).
 *   - Reads come exclusively from canonical endpoints via the
 *     `useCaseWorkflow` server-state hook.
 *   - Writes flow through the canonical HTTP write-doors only (R-5).
 *
 * RBAC:
 *   - READ_ROLES (server-enforced): clinic_admin, clinic_coordinator,
 *     coordinator, clinic_doctor, doctor, clinic_after_sales,
 *     clinic_sales, sales_rep, platform_admin, treatx_admin.
 *     Patient is EXPLICITLY excluded (R-13 — HTTP 403 forbidden_role).
 *   - WRITE_ROLES (server-enforced + FE-gated display): clinic_admin,
 *     clinic_coordinator, coordinator, clinic_after_sales, clinic_sales,
 *     platform_admin, treatx_admin.
 *   - FE hides write controls for non-write roles; server is the sole
 *     enforcer.
 *
 * SCOPE (FE-WIRING-1B):
 *   - Read canonical milestones + tasks.
 *   - Canonical milestone status transitions.
 *   - Canonical task create / transition / assign / transfer / suppress.
 *   - Read append-only `assignment_log` for a task (transfer history).
 *   - Loading / empty / error states.
 *   - Minimal status badges + action affordances. No redesign.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { formatDateLocale } from '../../../utils/dateFormat';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Loader2, AlertCircle, ChevronDown, ChevronUp,
  ClipboardList, Plus, History, X, CheckCircle2, Briefcase,
  Workflow as WorkflowIcon,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Toaster } from '../../../components/Toaster';
import { toast } from '../../../hooks/use-toast';
import PatientCasePlan from './PatientCasePlan';
import { useAuth } from '../../../context/AuthContext';
import {
  useCaseWorkflow,
  MILESTONE_STATUS_DISPLAY,
  TASK_STATUS_DISPLAY,
  MILESTONE_NEXT_STATUSES,
  TASK_NEXT_STATUSES,
  TASK_WRITE_ROLES,
} from '../../../hooks/useCaseWorkflow';
// WORKFLOW-OPS-001-PHASE-2A (P0-B): operational assignment / reassignment
// dialog. Replaces the previous window.prompt-based flow with a proper
// agent-aware dialog. Backend authority unchanged.
import AssignTaskDialog from '../../../components/AssignTaskDialog';
import { api } from '../../../utils/api';

/**
 * Resolve the active role for FE-side write-gate display.
 *
 * Reads (in order):
 *   1. patient AuthContext user (legacy patient tokens),
 *   2. unified-login user stored at `treatx_unified_user`.
 *
 * The server is the SOLE authority — this is purely for hiding disabled
 * controls. The server will return HTTP 403 forbidden_role for any
 * unauthorised mutation regardless of what FE renders.
 */
const useActiveRole = (authUser) => {
  if (authUser && (authUser.role || authUser.type)) {
    return String(authUser.role || authUser.type).toLowerCase();
  }
  try {
    const raw = localStorage.getItem('treatx_unified_user');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return String(parsed.role || parsed.type || '').toLowerCase();
  } catch {
    return '';
  }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return formatDateLocale(iso);
  } catch {
    return iso;
  }
};

const NextStatusPicker = ({ current, allowed, onPick, disabled, testId }) => {
  const { t } = useTranslation();
  if (!allowed || allowed.length === 0) return null;
  return (
    <select
      data-testid={testId}
      disabled={disabled}
      value=""
      onChange={(e) => {
        if (e.target.value) onPick(e.target.value);
        e.target.value = '';
      }}
      className="text-xs px-2 py-1 rounded border bg-white disabled:opacity-50"
    >
      <option value="">{t('provider.caseWorkflow.setStatusPlaceholder')}</option>
      {allowed.map((s) => (
        <option key={s} value={s}>{s}</option>
      ))}
    </select>
  );
};

const ClinicCaseWorkflow = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const role = useActiveRole(user);
  const canWrite = TASK_WRITE_ROLES.has(role);

  const {
    loading, error, milestones, tasks, refetch,
    transitionMilestone, createTask, transitionTask,
    assignTask, transferTask, suppressTask, fetchTaskHistory,
    addDocumentRequirement, addSignatureRequirement,
    satisfyRequirement, cancelRequirement,
  } = useCaseWorkflow(caseId);

  const [expandedMilestoneId, setExpandedMilestoneId] = useState(null);
  const [historyForTaskId, setHistoryForTaskId] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // WORKFLOW-OPS-001-PHASE-2A: assignment / reassignment dialog state.
  const [assignDialogState, setAssignDialogState] = useState({ open: false, mode: 'assign', task: null });

  // Cache of clinic agents → resolve assignee user_id to a human name on
  // each task row. Best-effort; falls open if the role lacks the
  // `clinic.manage_staff` permission required by /api/clinic/agents.
  const [agentDirectory, setAgentDirectory] = useState({});

  // M16B-07D-B2 — Journey Planner reachability gate.
  // Eligible roles match the existing `/clinic/cases/:caseId/journey-planner`
  // route guard in App.js (provider_admin + provider_coordinator).  We
  // extend defensively to legacy synonyms used by older tokens but
  // EXCLUDE every sales rep variant (locked product rule: "Sales Rep
  // must not become planner owner").
  const PLANNER_ELIGIBLE_ROLES = new Set([
    'provider_admin', 'provider_coordinator',
    'clinic_admin', 'clinic_coordinator', 'coordinator',
    'platform_admin', 'treatx_admin',
  ]);
  const isPlannerEligible = PLANNER_ELIGIBLE_ROLES.has(role);
  const [plannerCtx, setPlannerCtx] = useState(null);
  const [plannerCtxLoading, setPlannerCtxLoading] = useState(false);
  useEffect(() => {
    if (!caseId || !isPlannerEligible) return undefined;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      setPlannerCtxLoading(true);
      try {
        const { data } = await api.get(`/clinic/cases/${caseId}/planner-context`);
        if (!cancelled) setPlannerCtx(data);
      } catch {
        if (!cancelled) setPlannerCtx({ payment: { received: false } });
      } finally {
        if (!cancelled) setPlannerCtxLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [caseId, isPlannerEligible]);
  const plannerPaymentReceived = !!plannerCtx?.payment?.received;
  useEffect(() => {
    if (!canWrite) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/clinic/agents');
        if (cancelled) return;
        const map = {};
        (data?.agents || []).forEach((a) => {
          const k = a.user_id || a.agent_id;
          if (k) map[k] = a;
        });
        setAgentDirectory(map);
      } catch {
        /* fail-open — assignee shows the raw user_id */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canWrite]);
  const resolveAssigneeName = (uid) => {
    if (!uid) return null;
    const a = agentDirectory[uid];
    if (a && (a.display_name || a.name)) return a.display_name || a.name;
    return null;
  };

  // Create-task inline form per milestone
  const [createDraft, setCreateDraft] = useState({}); // milestoneId → { title, assigned_role }

  // Group canonical tasks by milestone_instance_id (NEVER fabricate; just bucket).
  const tasksByMilestone = useMemo(() => {
    const m = {};
    (tasks || []).forEach((t) => {
      const mid = t.milestone_instance_id;
      if (!mid) return;
      if (!m[mid]) m[mid] = [];
      m[mid].push(t);
    });
    return m;
  }, [tasks]);

  // ── handlers ─────────────────────────────────────────────────────────
  const handleMilestoneTransition = async (mid, targetStatus) => {
    try {
      await transitionMilestone(mid, targetStatus);
      toast({ title: t('provider.caseWorkflow.toastJourneyStepUpdated'), description: `→ ${targetStatus}` });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || t('provider.caseWorkflow.transitionFailedGeneric');
      toast({
        title: t('provider.caseWorkflow.toastTransitionRejected'),
        description: typeof detail === 'string' ? detail : JSON.stringify(detail),
        variant: 'destructive',
      });
    }
  };

  const handleTaskTransition = async (taskId, targetStatus) => {
    try {
      await transitionTask(taskId, targetStatus);
      toast({ title: t('provider.caseWorkflow.toastTaskUpdated'), description: `→ ${targetStatus}` });
    } catch (e) {
      // R6-IMPL-003A: surface the precondition-block payload distinctly.
      const detail = e?.response?.data?.detail;
      let body = null;
      if (typeof detail === 'string') {
        try { body = JSON.parse(detail.replace(/'/g, '"')); } catch { body = null; }
      } else if (detail && typeof detail === 'object') {
        body = detail;
      }
      if (body && body.error === 'preconditions_unmet') {
        const missingDocs = (body.missing_documents || []).map((d) => d.title || d.requirement_id).join(', ');
        const missingSigs = (body.missing_signatures || []).map((d) => d.title || d.requirement_id).join(', ');
        toast({
          title: t('provider.caseWorkflow.toastTaskBlockedTitle'),
          description: [
            missingDocs && t('provider.caseWorkflow.toastDocsPending', { list: missingDocs }),
            missingSigs && t('provider.caseWorkflow.toastSigsPending', { list: missingSigs }),
          ].filter(Boolean).join(' · '),
          variant: 'destructive',
        });
        return;
      }
      const desc = typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : e?.message || t('provider.caseWorkflow.transitionFailedGeneric'));
      toast({
        title: t('provider.caseWorkflow.toastTaskTransitionRejected'),
        description: desc,
        variant: 'destructive',
      });
    }
  };

  // R6-IMPL-003A — precondition handlers
  const handleAddDocRequirement = async (taskId) => {
    const title = window.prompt(t('provider.caseWorkflow.promptDocTitle'), '');
    if (!title || !title.trim()) return;
    try {
      await addDocumentRequirement(taskId, { title: title.trim() });
      toast({ title: t('provider.caseWorkflow.toastDocReqAdded') });
    } catch (e) {
      toast({ title: t('provider.caseWorkflow.toastCannotAddReq'), description: e?.message || t('provider.caseWorkflow.genericFailed'), variant: 'destructive' });
    }
  };
  const handleAddSigRequirement = async (taskId) => {
    const title = window.prompt(t('provider.caseWorkflow.promptSigTitle'), '');
    if (!title || !title.trim()) return;
    try {
      await addSignatureRequirement(taskId, { title: title.trim() });
      toast({ title: t('provider.caseWorkflow.toastSigReqAdded') });
    } catch (e) {
      toast({ title: t('provider.caseWorkflow.toastCannotAddReq'), description: e?.message || t('provider.caseWorkflow.genericFailed'), variant: 'destructive' });
    }
  };
  const handleSatisfyRequirement = async (taskId, requirementId) => {
    if (!window.confirm(t('provider.caseWorkflow.confirmSatisfy'))) return;
    try {
      await satisfyRequirement(taskId, requirementId);
      toast({ title: t('provider.caseWorkflow.toastReqSatisfied') });
    } catch (e) {
      toast({ title: t('provider.caseWorkflow.toastCannotSatisfy'), description: e?.message || t('provider.caseWorkflow.genericFailed'), variant: 'destructive' });
    }
  };
  const handleCancelRequirement = async (taskId, requirementId) => {
    if (!window.confirm(t('provider.caseWorkflow.confirmCancel'))) return;
    try {
      await cancelRequirement(taskId, requirementId);
      toast({ title: t('provider.caseWorkflow.toastReqCancelled') });
    } catch (e) {
      toast({ title: t('provider.caseWorkflow.toastCannotCancel'), description: e?.message || t('provider.caseWorkflow.genericFailed'), variant: 'destructive' });
    }
  };

  const handleTaskCreate = async (milestoneId) => {
    const draft = createDraft[milestoneId] || {};
    if (!draft.title || !draft.title.trim()) {
      toast({ title: t('provider.caseWorkflow.toastTitleRequired'), variant: 'destructive' });
      return;
    }
    try {
      await createTask({
        milestone_instance_id: milestoneId,
        title: draft.title.trim(),
        assigned_role: draft.assigned_role || undefined,
      });
      setCreateDraft((p) => ({ ...p, [milestoneId]: { title: '', assigned_role: '' } }));
      toast({ title: t('provider.caseWorkflow.toastTaskCreated') });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || t('provider.caseWorkflow.createFailedGeneric');
      toast({
        title: t('provider.caseWorkflow.toastTaskCreateRejected'),
        description: typeof detail === 'string' ? detail : JSON.stringify(detail),
        variant: 'destructive',
      });
    }
  };

  // WORKFLOW-OPS-001-PHASE-2A (P0-B): open the agent-aware dialog
  // instead of window.prompt. Submit is wired below in the JSX.
  const handleTaskAssign = (taskId) => {
    const task = (tasks || []).find((t) => (t.milestone_task_id || t.id) === taskId);
    setAssignDialogState({ open: true, mode: 'assign', task });
  };

  const handleTaskTransfer = (taskId) => {
    const task = (tasks || []).find((t) => (t.milestone_task_id || t.id) === taskId);
    setAssignDialogState({ open: true, mode: 'transfer', task });
  };

  const submitAssignDialog = async (body) => {
    const task = assignDialogState.task;
    if (!task) return;
    const taskId = task.milestone_task_id || task.id;
    if (assignDialogState.mode === 'transfer') {
      await transferTask(taskId, body);
      toast({ title: t('provider.caseWorkflow.toastTaskReassigned') });
    } else {
      await assignTask(taskId, body);
      toast({ title: t('provider.caseWorkflow.toastTaskAssigned') });
    }
  };

  const handleTaskSuppress = async (taskId) => {
    const reason = window.prompt(t('provider.caseWorkflow.promptSuppressReason'));
    if (!reason) return;
    try {
      await suppressTask(taskId, { suppression_reason: reason });
      toast({ title: t('provider.caseWorkflow.toastTaskSuppressed') });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || t('provider.caseWorkflow.suppressFailedGeneric');
      toast({ title: t('provider.caseWorkflow.toastSuppressRejected'), description: String(detail), variant: 'destructive' });
    }
  };

  const openHistory = async (taskId) => {
    setHistoryForTaskId(taskId);
    setHistoryLoading(true);
    try {
      const res = await fetchTaskHistory(taskId);
      setHistoryRows(Array.isArray(res?.history) ? res.history : []);
    } catch {
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50"
        data-testid="clinic-case-workflow-loading"
      >
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail || error?.response?.data?.message;
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50"
        data-testid="clinic-case-workflow-error"
      >
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">
              {status === 403 ? t('provider.caseWorkflow.accessDeniedTitle') : t('provider.caseWorkflow.unableToLoadTitle')}
            </h2>
            <p className="text-slate-600 mb-4 break-words">
              {status === 403
                ? t('provider.caseWorkflow.accessDeniedBody')
                : typeof detail === 'string' ? detail : t('provider.caseWorkflow.unableToLoadRetry')}
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => navigate(-1)}>{t('provider.caseWorkflow.back')}</Button>
              {status !== 403 && <Button onClick={refetch}>{t('provider.caseWorkflow.retry')}</Button>}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="clinic-case-workflow-page">
      <Toaster />

      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              data-testid="clinic-case-workflow-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('provider.caseWorkflow.back')}
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                {t('provider.caseWorkflow.patientCase')}
              </h1>
              <p
                className="text-xs text-slate-500 font-mono"
                data-testid="clinic-case-workflow-case-id"
              >
                {caseId}
              </p>
            </div>
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-3" data-testid="clinic-case-workflow-role">
            {isPlannerEligible && (
              plannerPaymentReceived ? (
                <Button
                  data-testid="clinic-case-workflow-open-planner-btn"
                  onClick={() => navigate(`/clinic/cases/${caseId}/journey-planner`)}
                  className="bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5"
                  size="sm"
                >
                  <WorkflowIcon className="h-4 w-4" />
                  {t('provider.caseWorkflow.openJourneyPlanner')}
                </Button>
              ) : (
                <span
                  data-testid="clinic-case-workflow-planner-locked"
                  className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 border border-slate-200 bg-slate-50 rounded px-2 py-1"
                  title={t('provider.caseWorkflow.plannerLockedTitle')}
                >
                  <WorkflowIcon className="h-3.5 w-3.5" />
                  {t('provider.caseWorkflow.plannerPrefix')} {plannerCtxLoading ? t('provider.caseWorkflow.plannerCheckingPayment') : t('provider.caseWorkflow.plannerAwaitingPayment')}
                </span>
              )
            )}
            <span>
              {t('provider.caseWorkflow.roleLabel')} <span className="font-medium">{role || t('provider.caseWorkflow.roleUnknown')}</span>
              {!canWrite && (
                <span className="ml-2 text-amber-600">{t('provider.caseWorkflow.readOnlyBadge')}</span>
              )}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        {/* Phase 3 — Patient Case Plan (snapshot + overrides) */}
        <section data-testid="case-workflow-case-plan-section" className="space-y-2">
          <div className="flex items-center gap-2">
            <h2 className="text-[13px] font-semibold text-slate-900">{t('provider.caseWorkflow.casePlanSectionTitle')}</h2>
            <span className="text-[10.5px] text-slate-400">
              {t('provider.caseWorkflow.casePlanSectionSubtitle')}
            </span>
          </div>
          <PatientCasePlan caseId={caseId} />
        </section>

        {milestones.length === 0 ? (
          <Card data-testid="clinic-case-workflow-empty">
            <CardContent className="p-8 text-center">
              <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-slate-700 mb-2">
                {t('provider.caseWorkflow.noStepsTitle')}
              </h2>
              <p className="text-slate-500">
                {t('provider.caseWorkflow.noStepsBody')}
              </p>
            </CardContent>
          </Card>
        ) : (
          milestones.map((m) => {
            const mid = m.id || m.milestone_instance_id;
            const isExpanded = expandedMilestoneId === mid;
            const mStatusDisplay =
              MILESTONE_STATUS_DISPLAY[m.status] || MILESTONE_STATUS_DISPLAY.pending;
            const milestoneTasks = tasksByMilestone[mid] || [];
            const allowedNext = MILESTONE_NEXT_STATUSES[m.status] || [];

            return (
              <Card
                key={mid}
                className="overflow-hidden"
                data-testid={`milestone-row-${mid}`}
              >
                <CardHeader
                  className="cursor-pointer hover:bg-slate-50 flex flex-row items-center gap-3"
                  onClick={() => setExpandedMilestoneId(isExpanded ? null : mid)}
                  data-testid={`milestone-toggle-${mid}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">
                        {t('provider.caseWorkflow.milestoneLabel', { step: m.step ?? t('provider.caseWorkflow.milestoneStepDash') })}
                      </span>
                      <Badge
                        variant="outline"
                        className={mStatusDisplay.badgeClass}
                        data-testid={`milestone-status-${mid}`}
                      >
                        {mStatusDisplay.label}
                      </Badge>
                      <Badge variant="outline" className="bg-slate-50 text-slate-500 text-xs">
                        {milestoneTasks.length === 1
                          ? t('provider.caseWorkflow.taskCountSingular', { count: milestoneTasks.length })
                          : t('provider.caseWorkflow.taskCountPlural', { count: milestoneTasks.length })}
                      </Badge>
                    </div>
                    <CardTitle className="text-base text-slate-800 mt-1 truncate">
                      {m.title || t('provider.caseWorkflow.untitledMilestone')}
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">
                      {t('provider.caseWorkflow.scheduledLabel', { date: fmtDate(m.scheduledDate) })}
                      {m.completedAt && (
                        <span className="ml-3 text-green-700">
                          <CheckCircle2 className="inline h-3 w-3 mr-1" />
                          {t('provider.caseWorkflow.achievedLabel', { date: fmtDate(m.completedAt) })}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                    {canWrite && (
                      <NextStatusPicker
                        current={m.status}
                        allowed={allowedNext}
                        onPick={(s) => handleMilestoneTransition(mid, s)}
                        disabled={!canWrite}
                        testId={`milestone-status-picker-${mid}`}
                      />
                    )}
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent
                    className="bg-slate-50/40 border-t space-y-4"
                    data-testid={`milestone-detail-${mid}`}
                  >
                    {m.description && (
                      <p className="text-sm text-slate-600 mt-3">{m.description}</p>
                    )}

                    {/* Tasks for this milestone */}
                    <div className="space-y-2" data-testid={`milestone-tasks-${mid}`}>
                      <h4 className="text-sm font-semibold text-slate-700 mt-2">
                        {t('provider.caseWorkflow.operationalTasks')}
                      </h4>
                      {milestoneTasks.length === 0 ? (
                        <p
                          className="text-xs text-slate-400 italic"
                          data-testid={`milestone-tasks-empty-${mid}`}
                        >
                          {t('provider.caseWorkflow.noTasksYet')}
                        </p>
                      ) : (
                        milestoneTasks.map((task) => {
                          const tid = task.id || task.milestone_task_id;
                          const tStatusDisplay =
                            TASK_STATUS_DISPLAY[task.status] || TASK_STATUS_DISPLAY.assigned;
                          const tAllowed = TASK_NEXT_STATUSES[task.status] || [];
                          return (
                            <div
                              key={tid}
                              className="bg-white rounded border border-slate-200 p-3"
                              data-testid={`task-row-${tid}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-slate-800 truncate">
                                    {task.title || t('provider.caseWorkflow.untitledTask')}
                                  </p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {t('provider.caseWorkflow.taskRoleLabel')} <span className="font-medium">{task.assigned_role || t('provider.caseWorkflow.dashSymbol')}</span>
                                    <span className="mx-2">·</span>
                                    {t('provider.caseWorkflow.taskAssigneeLabel')} <span className="font-medium" data-testid={`task-assignee-${tid}`}>
                                      {resolveAssigneeName(task.assigned_to_user_id) || (
                                        task.assigned_to_user_id
                                          ? <span className="font-mono">{task.assigned_to_user_id.slice(0, 8)}…</span>
                                          : <span className="text-amber-600">{t('provider.caseWorkflow.taskUnassigned')}</span>
                                      )}
                                    </span>
                                    <span className="mx-2">·</span>
                                    {t('provider.caseWorkflow.taskDueLabel')} {fmtDate(task.dueDate || task.due_date)}
                                  </p>
                                </div>
                                <Badge
                                  variant="outline"
                                  className={tStatusDisplay.badgeClass}
                                  data-testid={`task-status-${tid}`}
                                >
                                  {tStatusDisplay.label}
                                </Badge>
                              </div>

                              {canWrite && (
                                <div className="flex flex-wrap items-center gap-2 mt-3 text-xs">
                                  <NextStatusPicker
                                    current={task.status}
                                    allowed={tAllowed}
                                    onPick={(s) => handleTaskTransition(tid, s)}
                                    testId={`task-status-picker-${tid}`}
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleTaskAssign(tid)}
                                    data-testid={`task-assign-btn-${tid}`}
                                  >
                                    {t('provider.caseWorkflow.assign')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleTaskTransfer(tid)}
                                    data-testid={`task-transfer-btn-${tid}`}
                                  >
                                    {t('provider.caseWorkflow.transfer')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleTaskSuppress(tid)}
                                    data-testid={`task-suppress-btn-${tid}`}
                                  >
                                    {t('provider.caseWorkflow.suppress')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openHistory(tid)}
                                    data-testid={`task-history-btn-${tid}`}
                                  >
                                    <History className="h-3 w-3 mr-1" />
                                    {t('provider.caseWorkflow.history')}
                                  </Button>
                                  {/* R6-IMPL-003A precondition write-doors */}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleAddDocRequirement(tid)}
                                    data-testid={`task-add-doc-req-${tid}`}
                                  >
                                    {t('provider.caseWorkflow.addDocReq')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleAddSigRequirement(tid)}
                                    data-testid={`task-add-sig-req-${tid}`}
                                  >
                                    {t('provider.caseWorkflow.addSigReq')}
                                  </Button>
                                </div>
                              )}

                              {/* R6-IMPL-003A — precondition panel */}
                              {(() => {
                                const pc = task.preconditions || {};
                                const docs = pc.required_documents || [];
                                const sigs = pc.required_signatures || [];
                                if (!docs.length && !sigs.length) return null;
                                const pending = (arr) => arr.filter((r) => r.status === 'pending').length;
                                const totalPending = pending(docs) + pending(sigs);
                                return (
                                  <div
                                    className={`mt-3 rounded border p-2 text-xs ${totalPending ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}
                                    data-testid={`task-preconditions-${tid}`}
                                  >
                                    <div className="font-medium text-slate-700 mb-1">
                                      {t('provider.caseWorkflow.requirementsHeader', { pending: totalPending, total: docs.length + sigs.length })}
                                    </div>
                                    {[...docs.map((r) => ({ ...r, kind: 'document' })), ...sigs.map((r) => ({ ...r, kind: 'signature' }))].map((r) => (
                                      <div
                                        key={r.requirement_id}
                                        className="flex items-center justify-between gap-2 py-0.5"
                                        data-testid={`task-precondition-${r.requirement_id}`}
                                      >
                                        <span className="truncate">
                                          <span className="font-mono text-[10px] mr-1 text-slate-500">
                                            {r.kind === 'document' ? t('provider.caseWorkflow.kindDoc') : t('provider.caseWorkflow.kindSig')}
                                          </span>
                                          <span>{r.title || r.requirement_id}</span>
                                          <span className={`ml-2 inline-block px-1.5 rounded text-[10px] ${r.status === 'satisfied' ? 'bg-emerald-200 text-emerald-800' : r.status === 'cancelled' ? 'bg-slate-200 text-slate-600' : 'bg-amber-200 text-amber-800'}`}>
                                            {r.status}
                                          </span>
                                        </span>
                                        {canWrite && r.status === 'pending' && (
                                          <span className="flex gap-1">
                                            <button
                                              className="text-emerald-700 hover:underline"
                                              onClick={() => handleSatisfyRequirement(tid, r.requirement_id)}
                                              data-testid={`task-precondition-satisfy-${r.requirement_id}`}
                                            >
                                              {t('provider.caseWorkflow.reqSatisfy')}
                                            </button>
                                            <button
                                              className="text-slate-500 hover:underline"
                                              onClick={() => handleCancelRequirement(tid, r.requirement_id)}
                                              data-testid={`task-precondition-cancel-${r.requirement_id}`}
                                            >
                                              {t('provider.caseWorkflow.reqCancel')}
                                            </button>
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Create task inline (write roles only) */}
                    {canWrite && (
                      <div
                        className="bg-white rounded border border-dashed border-slate-300 p-3"
                        data-testid={`task-create-form-${mid}`}
                      >
                        <h5 className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                          <Plus className="h-3 w-3" /> {t('provider.caseWorkflow.newTaskCardTitle')}
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <div className="md:col-span-2">
                            <Label className="text-xs">{t('provider.caseWorkflow.titleLabel')}</Label>
                            <Input
                              value={(createDraft[mid] || {}).title || ''}
                              onChange={(e) =>
                                setCreateDraft((p) => ({
                                  ...p,
                                  [mid]: { ...(p[mid] || {}), title: e.target.value },
                                }))
                              }
                              placeholder={t('provider.caseWorkflow.titlePlaceholder')}
                              data-testid={`task-create-title-${mid}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">{t('provider.caseWorkflow.assignedRoleLabel')}</Label>
                            <Input
                              value={(createDraft[mid] || {}).assigned_role || ''}
                              onChange={(e) =>
                                setCreateDraft((p) => ({
                                  ...p,
                                  [mid]: { ...(p[mid] || {}), assigned_role: e.target.value },
                                }))
                              }
                              placeholder={t('provider.caseWorkflow.assignedRolePlaceholder')}
                              data-testid={`task-create-role-${mid}`}
                            />
                          </div>
                        </div>
                        <div className="mt-2 flex justify-end">
                          <Button
                            size="sm"
                            onClick={() => handleTaskCreate(mid)}
                            data-testid={`task-create-submit-${mid}`}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            {t('provider.caseWorkflow.createTask')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })
        )}
      </main>

      {/* Assignment-log history drawer */}
      {historyForTaskId && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end justify-end z-50"
          onClick={() => setHistoryForTaskId(null)}
          data-testid="task-history-overlay"
        >
          <div
            className="bg-white w-full max-w-md h-full p-6 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="task-history-panel"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <History className="h-4 w-4" />
                {t('provider.caseWorkflow.taskHistoryDrawerTitle')}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setHistoryForTaskId(null)}
                data-testid="task-history-close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-500 mb-4 font-mono break-all">
              {historyForTaskId}
            </p>
            {historyLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            ) : historyRows.length === 0 ? (
              <p className="text-sm text-slate-400 italic" data-testid="task-history-empty">
                {t('provider.caseWorkflow.noTransferHistory')}
              </p>
            ) : (
              <ul className="space-y-3 text-sm" data-testid="task-history-list">
                {historyRows.map((row, idx) => (
                  <li
                    key={row.assignment_log_id || idx}
                    className="border-l-2 border-blue-300 pl-3"
                    data-testid={`task-history-row-${idx}`}
                  >
                    <p className="font-medium text-slate-800">
                      {row.reason || t('provider.caseWorkflow.assignmentLogFallback')}
                    </p>
                    <p className="text-xs text-slate-500">
                      {fmtDate(row.created_at)} {t('provider.caseWorkflow.byActor', { actor: row.actor || row.assigned_by_role || t('provider.caseWorkflow.actorSystem') })}
                    </p>
                    <p className="text-xs text-slate-600">
                      {t('provider.caseWorkflow.roleArrow', { role: row.new_assigned_role || row.assigned_role || t('provider.caseWorkflow.dashSymbol') })}
                      {row.new_assigned_to_user_id && (
                        <> · <span className="font-mono">{row.new_assigned_to_user_id}</span></>
                      )}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* WORKFLOW-OPS-001-PHASE-2A: agent-aware assign / reassign dialog */}
      <AssignTaskDialog
        open={assignDialogState.open}
        onOpenChange={(o) => setAssignDialogState((s) => ({ ...s, open: o }))}
        mode={assignDialogState.mode}
        task={assignDialogState.task}
        onSubmit={submitAssignDialog}
      />
    </div>
  );
};

export default ClinicCaseWorkflow;
